// @flow

import { useEffect, useMemo } from 'react';

import { Controller, useFormContext } from 'react-hook-form';

import { useDispatch } from 'react-redux';
import { Checkbox, Loader, Text, View } from 'reshaped';

import {
  selectContracts,
  computeLabel,
  contractsResource,
  fetchContracts,
} from '@gandi/contracts';
import { useCountry } from '@gandi/react-places';
import { Link } from '@gandi/reshaped';
import { useTranslator } from '@gandi/translate';

import { useAppSelector } from 'src/hooks/useAppSelector';

export function MSACheckbox(): React.MixedElement | null {
  const { __ } = useTranslator();
  const dispatch = useDispatch();

  const form = useFormContext();
  const country = form.watch('country');

  const { buId } = useCountry(country);

  const contractParams = useMemo(
    () => ({
      buId: buId ? buId.toString() : undefined,
      contractId: 'g2',
      dedup: false,
    }),
    [buId],
  );
  const contractLabel = computeLabel(contractParams);

  const contracts = useAppSelector(state =>
    selectContracts(state, contractLabel),
  );

  const shouldFetchContracts = useAppSelector(state =>
    contractsResource.selectShouldFetch(state, contractLabel),
  );

  const isFetchingContracts = useAppSelector(state =>
    contractsResource.selectIsFetching(state, contractLabel),
  );

  const isLoadedContracts = useAppSelector(state =>
    contractsResource.selectIsLoaded(state, contractLabel),
  );

  const isFailureContracts = useAppSelector(state =>
    contractsResource.selectIsFailure(state, contractLabel),
  );

  useEffect(() => {
    if (shouldFetchContracts && contractParams.buId) {
      dispatch(fetchContracts(contractParams));
    }
  }, [contractParams, shouldFetchContracts, dispatch]);

  const isPending =
    !isLoadedContracts && (!isFailureContracts || isFetchingContracts);
  const hasContracts = contracts && contracts.length > 0;

  const contract = hasContracts ? contracts[0] : null;
  return (
    <div>
      <View paddingBlock={4}>
        <Text variant="body-2" weight="bold">
          {__('Contracts')}
        </Text>
      </View>
      <Controller
        name="msa"
        control={form.control}
        defaultValue={false}
        rules={{
          required: __(
            '<for-translators-use>%(count)s</for-translators-use>Please accept all contracts above to proceed.',
            { count: 1 },
          ),
        }}
        render={({ field, fieldState }) => {
          if (isPending) {
            return <Loader size="small" ariaLabel={__('Loading...')} />;
          }

          // No contract (or fetch failed): show an error instead of the checkbox.
          // The field remains registered, so `required` still blocks the submit.
          if (!contract) {
            return (
              <Text variant="body-2" color="critical">
                {__(
                  'An unexpected error occurred. Please refresh the page or try again later.',
                )}
              </Text>
            );
          }

          return (
            <>
              {/* $FlowFixMe[incompatible-type] Reshaped's Checkbox types attributes without `ref`, but it is forwarded at runtime */}
              <Checkbox
                name={field.name}
                checked={!!field.value}
                onChange={({ checked }) => field.onChange(checked)}
                attributes={{
                  ref: field.ref,
                }}
              >
                <View direction="row" gap={1} paddingBlock={1}>
                  <Link
                    href={contract?.href}
                    attributes={{
                      target: '_blank',
                    }}
                  >
                    <Text variant="body-2">
                      {contract.label || contract.description}
                    </Text>
                  </Link>
                </View>
              </Checkbox>
              {fieldState.error && (
                <Text variant="caption-1" color="critical">
                  {fieldState.error.message}
                </Text>
              )}
            </>
          );
        }}
      />
    </div>
  );
}
